import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import {
  PAIRED_OPTION_TYPES,
  GEM_TYPE,
  WING_TYPES,
  GEM_OPTION_CODES,
  GEM_LEGACY_OPTION_CODES,
  SKIN_PERCENT_CODES,
  BATTLE_LINES,
  DESCRIBED_BATTLE_LINES,
  OPTION_VALUE_HINTS,
  optionCodeLabel,
  battleLineLabel,
} from "layouts/templates/labels";

const INTEGER = /^-?\d+$/;

/** Lỗi nhập option (ô trống / không phải số nguyên), null nếu hợp lệ. */
export function optionInputError(options, values) {
  const bad = [...options, ...values].some((v) => !INTEGER.test(String(v).trim()));
  return bad ? "Mọi mã dòng và giá trị option phải là số nguyên" : null;
}

const SELECT_PROPS = { sx: { height: 44 } };
const BATTLE_CODES = [13, 14, 15, 16];
const WING_EXTRA_CODES = [...SKIN_PERCENT_CODES, 18, 2, 1];

function CodeSelect({ value, codes, onChange }) {
  const list = codes.includes(Number(value)) ? codes : [Number(value), ...codes];
  return (
    <MDInput
      select
      fullWidth
      label="Dòng"
      value={Number(value)}
      onChange={(e) => onChange(String(e.target.value))}
      SelectProps={SELECT_PROPS}
    >
      {list.map((code) => (
        <MenuItem key={code} value={code}>
          {code} · {optionCodeLabel(code)}
        </MenuItem>
      ))}
    </MDInput>
  );
}

CodeSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  codes: PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

function ValueInput({ label, value, onChange, helperText, min }) {
  return (
    <MDInput
      fullWidth
      type="number"
      label={label}
      value={value}
      helperText={helperText}
      inputProps={min === null ? {} : { min }}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

ValueInput.defaultProps = { helperText: null, min: null };
ValueInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  min: PropTypes.number,
};

function RemoveButton({ onClick, title }) {
  return (
    <IconButton size="small" color="error" onClick={onClick} title={title}>
      <Icon>delete</Icon>
    </IconButton>
  );
}

RemoveButton.propTypes = { onClick: PropTypes.func.isRequired, title: PropTypes.string.isRequired };

const percentText = (raw) => {
  const n = Number(raw);
  return Number.isFinite(n) ? `= ${n / 100}%` : null;
};

/** Một hiệu ứng trong trận của cánh / skin: 4 ô liền nhau 13 (mã hiệu ứng), 14 (lượt), 15 (giá trị), 16 (cho ai). */
function BattleBlock({ values, onChange, onRemove }) {
  const [line, turns, amount, target] = values;
  const lineId = Number(line);
  const lines = Object.keys(BATTLE_LINES).map(Number);
  const set = (index, v) => onChange(values.map((old, i) => (i === index ? v : old)));
  return (
    <MDBox p={1.5} borderRadius="md" sx={{ border: "1px solid", borderColor: "divider" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="button" fontWeight="medium">
          Hiệu ứng trong trận
        </MDTypography>
        <RemoveButton onClick={onRemove} title="Xoá hiệu ứng" />
      </MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <MDInput
            select
            fullWidth
            label="Hiệu ứng"
            value={lineId}
            onChange={(e) => set(0, String(e.target.value))}
            SelectProps={SELECT_PROPS}
            helperText={
              DESCRIBED_BATTLE_LINES.includes(lineId)
                ? null
                : "Có tác dụng nhưng mô tả cánh trong game không hiện dòng này"
            }
          >
            {(lines.includes(lineId) ? lines : [lineId, ...lines]).map((id) => (
              <MenuItem key={id} value={id}>
                {id} · {battleLineLabel(id)}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MDInput
            select
            fullWidth
            label="Cho ai"
            value={Number(target) === 1 ? 1 : 0}
            onChange={(e) => set(3, String(e.target.value))}
            SelectProps={SELECT_PROPS}
          >
            <MenuItem value={1}>Lên mình</MenuItem>
            <MenuItem value={0}>Lên đối thủ</MenuItem>
          </MDInput>
        </Grid>
        <Grid item xs={6} sm={3}>
          <ValueInput
            label="Số lượt"
            value={turns}
            min={1}
            helperText={Number(turns) > 100 ? "Cả trận" : "> 100 = cả trận"}
            onChange={(v) => set(1, v)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ValueInput
            label="Giá trị (% × 100, âm = giảm)"
            value={amount}
            helperText={percentText(amount)}
            onChange={(v) => set(2, v)}
          />
        </Grid>
      </Grid>
    </MDBox>
  );
}

BattleBlock.propTypes = {
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

/** Tách mảng option của cánh / skin thành các khối: hiệu ứng trong trận (4 dòng) hoặc một dòng lẻ. */
function wingBlocks(options) {
  const blocks = [];
  for (let i = 0; i < options.length; i += 1) {
    const isBattle =
      Number(options[i]) === 13 && BATTLE_CODES.every((code, k) => Number(options[i + k]) === code);
    blocks.push({ start: i, size: isBattle ? 4 : 1, battle: isBattle });
    if (isBattle) i += 3;
  }
  return blocks;
}

function PairedEditor({ type, options, values, onChange }) {
  const isGem = type === GEM_TYPE;
  const isWing = WING_TYPES.includes(type);
  const emit = (nextOptions, nextValues) => onChange(nextOptions, nextValues);
  const setValue = (index, v) =>
    emit(
      options,
      values.map((old, i) => (i === index ? v : old))
    );
  const setCode = (index, v) =>
    emit(
      options.map((old, i) => (i === index ? v : old)),
      values
    );
  const remove = (start, size) =>
    emit(
      options.filter((_, i) => i < start || i >= start + size),
      values.filter((_, i) => i < start || i >= start + size)
    );
  const add = (codes, vals) =>
    emit([...options, ...codes.map(String)], [...values, ...vals.map(String)]);

  const codes = isGem ? GEM_OPTION_CODES : WING_EXTRA_CODES;

  const lineRow = (i) => (
    <Grid container spacing={2} alignItems="center" key={`row-${i}`}>
      <Grid item xs={12} sm={6}>
        {isGem || isWing ? (
          <CodeSelect
            value={options[i]}
            codes={
              isGem && GEM_LEGACY_OPTION_CODES.includes(Number(options[i]))
                ? [...codes, ...GEM_LEGACY_OPTION_CODES]
                : codes
            }
            onChange={(v) => setCode(i, v)}
          />
        ) : (
          <ValueInput
            label="Mã dòng"
            value={options[i]}
            min={0}
            helperText={optionCodeLabel(Number(options[i]))}
            onChange={(v) => setCode(i, v)}
          />
        )}
      </Grid>
      <Grid item xs={10} sm={5}>
        <ValueInput
          label="Giá trị"
          value={values[i] ?? ""}
          min={isGem ? 0 : null}
          helperText={
            SKIN_PERCENT_CODES.includes(Number(options[i])) && !isGem
              ? percentText(values[i])
              : null
          }
          onChange={(v) => setValue(i, v)}
        />
      </Grid>
      <Grid item xs={2} sm={1}>
        <RemoveButton onClick={() => remove(i, 1)} title="Xoá dòng" />
      </Grid>
    </Grid>
  );

  const unusedGemCode = GEM_OPTION_CODES.find((code) => !options.map(Number).includes(code));

  return (
    <>
      {isWing
        ? wingBlocks(options).map((block) =>
            block.battle ? (
              <BattleBlock
                key={`battle-${block.start}`}
                values={values.slice(block.start, block.start + 4)}
                onChange={(next) =>
                  emit(
                    options,
                    values.map((old, i) =>
                      i >= block.start && i < block.start + 4 ? next[i - block.start] : old
                    )
                  )
                }
                onRemove={() => remove(block.start, 4)}
              />
            ) : (
              lineRow(block.start)
            )
          )
        : options.map((_, i) => lineRow(i))}
      {!options.length && (
        <MDTypography variant="caption" color="text">
          Chưa có dòng nào
        </MDTypography>
      )}
      <MDBox display="flex" gap={1} flexWrap="wrap">
        {isGem && (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={unusedGemCode === undefined}
            onClick={() => add([unusedGemCode], [0])}
          >
            <Icon>add</Icon>&nbsp;Thêm dòng chỉ số
          </MDButton>
        )}
        {isWing && (
          <>
            <MDButton size="small" variant="outlined" color="info" onClick={() => add([11], [0])}>
              <Icon>add</Icon>&nbsp;Thêm dòng % chỉ số
            </MDButton>
            <MDButton
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => add(BATTLE_CODES, [15, 99999, 0, 1])}
            >
              <Icon>bolt</Icon>&nbsp;Thêm hiệu ứng trong trận
            </MDButton>
          </>
        )}
        {!isGem && !isWing && (
          <MDButton size="small" variant="outlined" color="info" onClick={() => add([0], [0])}>
            <Icon>add</Icon>&nbsp;Thêm dòng
          </MDButton>
        )}
      </MDBox>
    </>
  );
}

PairedEditor.propTypes = {
  type: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  onChange: PropTypes.func.isRequired,
};

const splitList = (text) =>
  text
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Loại vật phẩm dùng danh sách giá trị tự do (mảnh, hộp quà, mực xăm...): sửa dạng "1, 2, 3". */
function ListEditor({ type, options, values, onChange }) {
  const [optionText, setOptionText] = useState(options.join(", "));
  const [valueText, setValueText] = useState(values.join(", "));
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <MDInput
          fullWidth
          label="Mã option"
          value={optionText}
          helperText="Thường giữ nguyên"
          onChange={(e) => {
            setOptionText(e.target.value);
            onChange(splitList(e.target.value), values);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={8}>
        <MDInput
          fullWidth
          label="Danh sách giá trị"
          value={valueText}
          helperText={OPTION_VALUE_HINTS[type] || "Các số cách nhau bởi dấu phẩy"}
          onChange={(e) => {
            setValueText(e.target.value);
            onChange(options, splitList(e.target.value));
          }}
        />
      </Grid>
    </Grid>
  );
}

ListEditor.propTypes = PairedEditor.propTypes;

function OptionEditor({ type, options, values, onChange }) {
  const paired = PAIRED_OPTION_TYPES.includes(type);
  let title = "Dòng option";
  if (type === GEM_TYPE) title = "Chỉ số ngọc (gốc, ở +0)";
  else if (WING_TYPES.includes(type)) title = "Dòng % chỉ số và hiệu ứng trong trận";
  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      <MDTypography variant="button" fontWeight="medium">
        {title}
      </MDTypography>
      {type === GEM_TYPE && (
        <Alert severity="info">
          Áp dụng ngay cho mọi viên ngọc đang khảm. Mỗi cấp cường hoá +15% giá trị gốc (+10 = 2,5
          lần).
        </Alert>
      )}
      {paired ? (
        <PairedEditor type={type} options={options} values={values} onChange={onChange} />
      ) : (
        <ListEditor type={type} options={options} values={values} onChange={onChange} />
      )}
      {optionInputError(options, values) && (
        <MDTypography variant="caption" color="error">
          {optionInputError(options, values)}
        </MDTypography>
      )}
    </MDBox>
  );
}

OptionEditor.propTypes = PairedEditor.propTypes;

export default OptionEditor;
